import React from "react";
import { Col, Container as GridContainer, Row } from "react-native-flex-grid";
import { Box, Button, Divider, FormattedCurrency, FormattedDate, H1, H3, HStack, Link, Text, Typography } from "components/ui";
import { Ban, CircleEllipsis, Ellipsis, MapPin, Repeat, ShoppingCart, Speech } from "lucide-react-native";
import { useToken } from "@gluestack-style/react";
import { truncateText } from "utils/globalFunctions";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { Menu, MenuOption, MenuOptions, MenuTrigger } from "react-native-popup-menu";
import Section from "components/general/Section";
type Props = {
	dataList: any;
	status?: string;
	isDashboard?: boolean;
};

const OrdersTable = ({ dataList, status, isDashboard = false }: Props) => {
	const isMobile = useIsMobile();
	const resolvedPrimarylight = useToken("colors", "primarylight");
	return (
		<GridContainer
			noPadding
			style={{
				marginLeft: 0,
				maxWidth: "100%",
				marginBottom: 30,
			}}
		>
			{dataList && dataList.length === 0 && (
				<Section gap={24}>
					<Box flex={2} gap={12}>
						<H3>No {status ? status : "orders"} history.</H3>
						<Typography sx={{ lineHeight: 21 }}>This page keeps track of all your {status ? status : "orders"} history.</Typography>
						<Typography sx={{ lineHeight: 21 }}>Right now, it's empty — once {status ? status : "orders"} is added, everything will appear here automatically.</Typography>
					</Box>
				</Section>
			)}
			{!isMobile && dataList.length !== 0 && (
				<Row style={{ marginLeft: 0, marginRight: 0, marginBottom: 14, width: "100%" }}>
					<Col>
						<Text>Order Id</Text>
					</Col>
					<Col>
						<Text>Site</Text>
					</Col>
					<Col>
						<Text>Ordered By</Text>
					</Col>
					<Col>
						<Text>Date</Text>
					</Col>
					<Col>
						<Text>Amount</Text>
					</Col>
					<Col>
						<Text>Status</Text>
					</Col>
					<Col>
						<Text>Type</Text>
					</Col>
				</Row>
			)}

			{dataList.map((order: any) => {
				var url = `/orders/${order.documentNumber}`;
				if (order.type == "cart") {
					url = `/cart/${order.id}`;
				}
				return (
					<Box position="relative" key={order.documentNumber}>
						<Link
							href={url}
							key={order.documentNumber}
							component={(hovered: boolean) => {
								var styleData = {
									backgroundColor: "white",
									marginLeft: 0,
									marginRight: 0,
									paddingHorizontal: isMobile ? 0 : 16,
									paddingVertical: 18,
									borderRadius: 8,
									borderWidth: 1,
									marginBottom: 14,
									borderColor: "#E1E9F0",
								};

								if (hovered) {
									// @ts-ignore
									styleData = { ...styleData, backgroundColor: "#E1E9F0" };
								}
								if (isMobile) {
									return (
										<Row style={styleData} key={order.documentNumber}>
											<Col>
												<Row style={{ paddingBottom: 6 }}>
													<Col style={{ flexDirection: "row", justifyContent: "space-between" }}>
														<HStack gap={8} alignItems="center">
															<ShoppingCart color={resolvedPrimarylight} />
															<Text>{order.documentNumber}</Text>
														</HStack>
														<HStack gap={8} alignItems="center">
															<FormattedDate sx={{ color: "$neutral400", fontSize: 14 }} variant="D/MMM/YY" date={order.dateUtc} />
															<Box
																opacity={0}
																borderWidth={1}
																borderColor="#8298AB"
																alignItems="center"
																borderRadius={"$full"}
																justifyContent="center"
																bg="#E1E9F0"
																width={"$full"}
																maxWidth={38}
																marginLeft="auto"
															>
																<Ellipsis color="#8298AB" />
															</Box>
														</HStack>
													</Col>
												</Row>

												<Row style={{ paddingBottom: 6 }}>
													<Col style={{ flexDirection: "row", justifyContent: "space-between" }}>
														<HStack gap={4}>
															<MapPin size={16} color={order.siteName ? resolvedPrimarylight : "#999999"} />
															<Text fontSize={12}>{order.siteName ? order.siteName : "Not available"} </Text>
														</HStack>
													</Col>
												</Row>
												<Row style={{ paddingBottom: 6 }}>
													<Col style={{ flexDirection: "row", justifyContent: "space-between" }}>
														<FormattedCurrency sx={{ fontWeight: "bold" }} amount={300} />
													</Col>
												</Row>
												<Row>
													<Col style={{ flexDirection: "row", justifyContent: "space-between" }}>
														<HStack gap={8}>
															<Text fontSize={12}>Ordered by: {order.orderedBy}</Text>
															<Divider orientation="vertical" bgColor="$neutral200" />
															<Text fontSize={12}>Type: {order.type}</Text>
														</HStack>
													</Col>
												</Row>
											</Col>
										</Row>
									);
								}
								return (
									<Row style={styleData} key={order.documentNumber}>
										<Col style={{ justifyContent: "center" }}>
											<HStack gap={8} alignItems="center">
												<ShoppingCart color={resolvedPrimarylight} />
												<Text>{order.documentNumber}</Text>
												{(order.type == "cart" || order.status == "Awaiting Approval") && (
													<Box justifyContent="center" alignItems="center" bg="$errormain" width={6} height={6} borderRadius={"$full"}>
														<Text color="$basicwhite"></Text>
													</Box>
												)}
											</HStack>
										</Col>
										<Col style={{ justifyContent: "center" }}>
											<HStack gap={8}>
												<MapPin size={24} color={resolvedPrimarylight} />
												<Text>{order.siteName ? truncateText(order.siteName, 15) : "Not available"}</Text>
											</HStack>
										</Col>
										<Col style={{ justifyContent: "center" }}>
											<HStack gap={8}>
												<Text color="$neutral400">{order.orderedBy}</Text>
											</HStack>
										</Col>
										<Col style={{ justifyContent: "center" }}>
											<HStack gap={8}>
												<FormattedDate variant="D/MMM/YY" date={order.dateUtc} />
											</HStack>
										</Col>
										<Col style={{ justifyContent: "center" }}>
											<HStack gap={8}>
												<FormattedCurrency sx={{ fontWeight: "bold" }} amount={order.total} />
											</HStack>
										</Col>
										<Col style={{ justifyContent: "center" }}>
											<HStack gap={8}>{order.status ? <Text>{order.status}</Text> : <Text color="$neutral400">Not available</Text>}</HStack>
										</Col>
										<Col style={{ justifyContent: "center" }}>
											<HStack gap={8}>
												<Text color="$neutral400">{order.type}</Text>
											</HStack>
										</Col>
									</Row>
								);
							}}
						></Link>

						{/* <Menu style={{ position: "absolute", top: 18, right: 16, shadowOpacity: 0 }} onSelect={(value) => alert(`Selected: ${value}`)}>
							<MenuTrigger>
								<Box borderWidth={1} borderColor="#8298AB" alignItems="center" borderRadius={"$full"} justifyContent="center" bg="#E1E9F0" width={"$full"} minWidth={38} maxWidth={38} marginLeft="auto">
									<Ellipsis color="#8298AB" />
								</Box>
							</MenuTrigger>
							<MenuOptions
								customStyles={{
									optionsWrapper: {
										borderRadius: 8,
									},
									optionsContainer: {
										borderRadius: 8,
										marginTop: 30,
									},
								}}
							>
								<MenuOption value={"Repeat order"}>
									<Box flexDirection="row" gap={8} paddingVertical={6} borderRadius={6} backgroundColor="#E1E9F0" alignItems="center" justifyContent="center">
										<Repeat color="black" width={16} height={16} />
										<Text>Repeat order</Text>
									</Box>
								</MenuOption>
								<MenuOption value={"Cancel order"}>
									<Box flexDirection="row" gap={8} paddingVertical={6} borderRadius={6} backgroundColor="#E1E9F0" alignItems="center" justifyContent="center">
										<Ban color="black" width={16} height={16} />
										<Text>Cancel order</Text>
									</Box>
								</MenuOption>
								<MenuOption value={"Get support"}>
									<Box flexDirection="row" gap={8} paddingVertical={6} borderRadius={6} backgroundColor="#E1E9F0" alignItems="center" justifyContent="center">
										<Speech color="black" width={16} height={16} />
										<Text>Get support</Text>
									</Box>
								</MenuOption>
							</MenuOptions>
						</Menu> */}
					</Box>
				);
			})}
		</GridContainer>
	);
};

export default OrdersTable;
